import React, { useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { StateNodeType } from '@sierralabs/workflow';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import { useParams } from 'react-router-dom';
import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileVersionReferenceCard from '../tasks/cards/TaskFileVersionReferenceCard';
import TaskStatusCard from '../tasks/cards/TaskStatusCard';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import {
  getStateNodeByStatus,
  useTaskEntityFields,
  useTaskWorkflow
} from '../workflow/Workflow.hooks';
import { useFindChangeOrderByTask } from './ChangeOrder.hook';
import { BaseChangeOrderCardProps, ChangeOrder } from './ChangeOrder.interface';
import { ChangeOrderDetailsCard } from './cards/ChangeOrderDetailsCard';
import { ChangeOrderInventoryAndPendingDispositionCard } from './cards/ChangeOrderInventoryAndPendingDispositionCard';
import { ChangeOrderProductCard } from './cards/ChangeOrderProductCard';
import { ChangeOrderManufacturingCard } from './cards/ChangeOrderManufacturingCard';
import { ChangeOrderLabelingCard } from './cards/ChangeOrderLabelingCard';
import { ChangeOrderSupplierCard } from './cards/ChangeOrderSupplierCard';
import { ChangeOrderQualityDocumentCard } from './cards/ChangeOrderQualityDocumentCard';
import { ChangeOrderOtherCard } from './cards/ChangeOrderOtherCard';
import ChangeOrderExportButton from './components/ChangeOrderExportButton';
import environment from '../../environments';
import { ChangeOrderITAssessmentCard } from './cards/ChangeOrderITAssessmentCard';
import { ChangeOrderImplementationCard } from './cards/ChangeOrderImplementationCard';
import { ChangeOrderVerificationCard } from './cards/ChangeOrderVerificationCard';
import { ChangeOrderTrainingAndCompetencyAssessmentCard } from './cards/ChangeOrderTrainingAndCompetencyAssessmentCard';
import { ChangeOrderImpactAssessmentCard } from './cards/ChangeOrderImpactAssessmentCard';
import { ChangeOrderReviewAndApproveChangeCard } from './cards/ChangeOrderReviewAndApproveChangeCard';
import { ChangeOrderVerificationOfChangeRecordsCard } from './cards/ChangeOrderVerificationOfChangeRecordsCard';
import { ChangeOrderImplementationActionsPlanningCard } from './cards/ChangeOrderImplementationActionsPlanningCard';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';

const ChangeOrderDetailPage: React.FC = () => {
  const { taskId } = useParams<{ taskId?: string }>();
  const { data, error, get } = useFindChangeOrderByTask(Number(taskId));
  const [changeOrder, setChangeOrder] = useState<ChangeOrder>();
  const workflow = useTaskWorkflow(changeOrder?.task);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder?.task);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const useCustomFields: boolean | undefined =
    environment.changeOrderConfig?.useCustomFields;
  const useMarsbioForm = environment.changeOrderConfig?.useMarsbioForm;

  const changeOrderCardProps: BaseChangeOrderCardProps = {
    changeOrder: changeOrder!,
    setErrorMessage,
    setSuccessMessage,
    onChange: (newChangeOrder: Partial<ChangeOrder>) => {
      if (changeOrder) {
        // notify task components to refresh
        changeOrder.task = { ...changeOrder.task };
        setChangeOrder({ ...changeOrder, ...newChangeOrder });
      }
    }
  };

  const taskCardProps = {
    onError: (error: FetchError) => setErrorMessage(error.message),
    onChange: (newTask: Partial<Task>) => {
      if (changeOrder) {
        Object.assign(changeOrder.task, newTask);
        changeOrderCardProps.onChange?.({});
      }
    }
  };

  useEffect(() => {
    get();
  }, [get]);

  useEffect(() => {
    data && setChangeOrder(data);
  }, [data]);

  useEffect(() => {
    error && setErrorMessage(error.message);
  }, [error]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        {changeOrder && <h2>{`CO-${changeOrder.id}`}</h2>}
        {changeOrder?.task && (
          <PageTemplateHeaderActions flex>
            <TaskStatusActions
              task={changeOrder.task}
              taskEntity={changeOrder}
              onChange={(task: Task) => {
                // check only if changeOrder task status changed
                if (changeOrder.task.status !== task.status) {
                  // if approved, refetch changeOrder to load fileReferences
                  const stateNode = getStateNodeByStatus(task.status, workflow);
                  const isFinal = stateNode?.type === StateNodeType.Final;
                  isFinal && get();
                }
                changeOrder.task = task;
                setChangeOrder({ ...changeOrder });
              }}
            />
            {changeOrder && (
              <div style={{ marginLeft: 10 }}>
                <ChangeOrderExportButton
                  changeOrder={changeOrder}
                  error={error => setErrorMessage(error.message)}
                />
              </div>
            )}
          </PageTemplateHeaderActions>
        )}
      </PageTemplateHeader>
      <PageTemplateDashboard>
        {changeOrder && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <ChangeOrderDetailsCard
                    {...changeOrderCardProps}
                    useCustomfields={useCustomFields}
                  />
                </Grid>
                {!useCustomFields ? null : useMarsbioForm ? (
                  <>
                    <Grid item xs={12}>
                      <ChangeOrderImpactAssessmentCard
                        {...changeOrderCardProps}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderReviewAndApproveChangeCard
                        {...changeOrderCardProps}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderImplementationActionsPlanningCard
                        {...changeOrderCardProps}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderVerificationOfChangeRecordsCard
                        {...changeOrderCardProps}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <ChangeOrderITAssessmentCard {...changeOrderCardProps} />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderInventoryAndPendingDispositionCard
                        {...changeOrderCardProps}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderProductCard {...changeOrderCardProps} />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderLabelingCard {...changeOrderCardProps} />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderManufacturingCard {...changeOrderCardProps} />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderSupplierCard {...changeOrderCardProps} />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderQualityDocumentCard
                        {...changeOrderCardProps}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderTrainingAndCompetencyAssessmentCard
                        {...changeOrderCardProps}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderOtherCard {...changeOrderCardProps} />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderImplementationCard
                        {...changeOrderCardProps}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ChangeOrderVerificationCard {...changeOrderCardProps} />
                    </Grid>
                  </>
                )}
                {changeOrder.task && (
                  <Grid item xs={12}>
                    <TaskAttachmentCard
                      {...taskCardProps}
                      task={changeOrder.task}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                {changeOrder.task && (
                  <>
                    <Grid item xs={12}>
                      <TaskStatusCard task={changeOrder.task} />
                    </Grid>
                    {changeOrder.task?.children &&
                      changeOrder.task.children.length > 0 && (
                        <Grid item xs={12}>
                          <SubTaskListCard
                            {...taskCardProps}
                            title='Stakeholders'
                            task={changeOrder.task}
                            disabled={isFieldDisabled('assignSubTasks')}
                          />
                        </Grid>
                      )}
                    <Grid item xs={12}>
                      <TaskAssignCard task={changeOrder.task} />
                    </Grid>
                    <Grid item xs={12}>
                      <TaskFileVersionReferenceCard
                        {...taskCardProps}
                        task={changeOrder.task}
                        disabled={isFieldDisabled('taskFileVersions')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TaskJiraIssueAssignCard
                        compact
                        task={changeOrder.task}
                        entity={{
                          title: changeOrder.name,
                          description: changeOrder.description
                        }}
                        jiraFieldColumns={
                          environment.changeOrderConfig?.visibleJiraFields
                        }
                        onChange={taskJiraIssues => {
                          // update changeOrder Task jira issues on change
                          changeOrder.task.jiraIssues = taskJiraIssues;
                          changeOrderCardProps.onChange?.({ ...changeOrder });
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
            <AppSnackbar
              severity='error'
              open={Boolean(errorMessage)}
              onClose={() => setErrorMessage('')}
            >
              {errorMessage}
            </AppSnackbar>
            <AppSnackbar
              severity='success'
              open={Boolean(successMessage)}
              onClose={() => setSuccessMessage('')}
            >
              {successMessage}
            </AppSnackbar>
          </Grid>
        )}
      </PageTemplateDashboard>
    </PageTemplate>
  );
};
export default ChangeOrderDetailPage;
